/**
 * Unified request helper + typed error for all backend endpoints.
 *
 * Why this file exists
 * --------------------
 * The existing HTTP clients (`@/lib/ats-https`, `@/lib/http`) use an
 * auth interceptor that `resolves` many 4xx responses as a *fulfilled*
 * promise whose body is the JSON envelope
 * `{ status: 4xx, errors: ['err_...'] }`. Callers must assert the
 * failure themselves (via `assertResolvedAtsNotHttpError`) before
 * unwrapping the `data` envelope — and many callers forget, producing
 * React Query states where `isSuccess === true` but the payload is
 * actually an error envelope.
 *
 * On top of that, responses come in three envelope shapes:
 *   1. `{ data: <payload>, errors?: string[] }` — Rails controllers
 *      that call `render_envelope`.
 *   2. Raw JSON — jbuilder views that skip the envelope.
 *   3. `{ status: 4xx, errors: [...] }` — authInterceptor's resolved
 *      4xx bodies.
 *
 * `callApi<T>()` normalises all three into:
 *   - Resolve with typed payload, OR
 *   - Reject with an `ApiError` carrying `status`, `code`, `errors`.
 *
 * Use this everywhere a React Query hook (or mutation) is being
 * written. React Query will surface `error` as an `ApiError` so UX
 * code can do `error instanceof ApiError && error.code === 'err_...'`.
 *
 * Migration posture
 * -----------------
 * New modules: use `callApi` directly.
 * Existing modules: migrate when the surrounding code is touched for
 * other reasons. Don't churn stable code just to move it.
 */
import type { AxiosRequestConfig } from 'axios';
import { http as atsHttp } from '@/lib/ats-https';
import { http as apiHttp } from '@/lib/http';

export type ApiClient = 'ats' | 'api';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Structured error thrown for every non-2xx backend response, for any
 * resolved 4xx body our auth interceptor swallows, and for envelopes
 * that carry `errors: [...]` alongside a 2xx status.
 *
 * `code` is the first item of `errors` — safe to compare directly:
 *   if (err.code === 'err_no_cal_connected') { ... }
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly errors: string[];
  readonly responseData: unknown;

  constructor(args: {
    message: string;
    status: number;
    errors: string[];
    code?: string;
    responseData: unknown;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.errors = args.errors;
    this.code = args.code;
    this.responseData = args.responseData;
  }
}

export interface CallApiOptions<TBody = unknown> {
  /** Which axios instance to use. Defaults to the ATS client. */
  client?: ApiClient;
  method?: HttpMethod;
  /** Path under the client's baseURL (no leading slash needed). */
  url: string;
  body?: TBody;
  params?: Record<string, unknown>;
  signal?: AbortSignal;
  /**
   * Pull `data` out of `{ data: ..., errors?: [] }` envelopes when
   * present. Default: true. Flip to false for endpoints whose payload
   * already includes a `data` key at its own schema level.
   */
  unwrapData?: boolean;
}

function firstError(errs: unknown): string[] {
  return Array.isArray(errs) ? errs.map((e) => String(e)) : [];
}

function extractApiErrorFromBody(body: unknown): ApiError | null {
  if (body === null || body === undefined) return null;

  if (typeof body === 'string') {
    // Empty body is a valid no-content response (e.g. 204 from a
    // DELETE / Rails `head :ok`). Axios resolves it as `""`; treating
    // that as an error would break every void endpoint. Non-empty
    // non-JSON bodies (HTML error pages, plain-text errors) still flag.
    if (body.trim().length === 0) return null;
    return new ApiError({
      status: 500,
      errors: [],
      responseData: body,
      message: `request_failed_non_json: ${body.trim().slice(0, 120)}`,
    });
  }

  if (typeof body !== 'object') return null;

  const env = body as { status?: unknown; errors?: unknown };

  // authInterceptor resolves 4xx responses as fulfilled with
  // { status: 4xx, errors: [...] }.
  if (typeof env.status === 'number' && env.status >= 400) {
    const errs = firstError(env.errors);
    return new ApiError({
      status: env.status,
      errors: errs,
      code: errs[0],
      responseData: body,
      message: errs[0] ?? `request_failed_status_${env.status}`,
    });
  }

  // 2xx with a non-empty errors list (partial-success shape).
  if (Array.isArray(env.errors) && env.errors.length > 0) {
    const errs = firstError(env.errors);
    return new ApiError({
      status: 400,
      errors: errs,
      code: errs[0],
      responseData: body,
      message: errs[0] ?? 'request_failed_errors',
    });
  }

  return null;
}

async function runRequest(
  client: ApiClient,
  method: HttpMethod,
  url: string,
  body: unknown,
  config: AxiosRequestConfig
): Promise<unknown> {
  const axiosClient = client === 'ats' ? atsHttp : apiHttp;
  if (method === 'GET') return axiosClient.get(url, config);
  if (method === 'DELETE') return axiosClient.delete(url, config);
  return axiosClient.request({ method, url, data: body, ...config });
}

export async function callApi<T>(opts: CallApiOptions): Promise<T> {
  const method = opts.method ?? 'GET';
  const client = opts.client ?? 'ats';
  const config: AxiosRequestConfig = {
    params: opts.params,
    signal: opts.signal,
  };

  let rawBody: unknown;
  try {
    rawBody = await runRequest(client, method, opts.url, opts.body, config);
  } catch (axiosErr) {
    const responseData = (axiosErr as { response?: { data?: unknown } })
      ?.response?.data;
    const fromBody =
      responseData !== undefined ? extractApiErrorFromBody(responseData) : null;
    throw fromBody ?? axiosErr;
  }

  const maybeError = extractApiErrorFromBody(rawBody);
  if (maybeError) throw maybeError;

  const unwrap = opts.unwrapData ?? true;
  if (
    unwrap &&
    rawBody !== null &&
    typeof rawBody === 'object' &&
    'data' in (rawBody as Record<string, unknown>)
  ) {
    return (rawBody as { data: T }).data;
  }

  return rawBody as T;
}

/**
 * Narrow helpers — thin shorthands used across endpoint modules to
 * keep call sites one-line readable.
 */
export const api = {
  get<T>(url: string, opts: Omit<CallApiOptions, 'url' | 'method'> = {}) {
    return callApi<T>({ ...opts, method: 'GET', url });
  },
  post<T>(
    url: string,
    body?: unknown,
    opts: Omit<CallApiOptions, 'url' | 'method' | 'body'> = {}
  ) {
    return callApi<T>({ ...opts, method: 'POST', url, body });
  },
  put<T>(
    url: string,
    body?: unknown,
    opts: Omit<CallApiOptions, 'url' | 'method' | 'body'> = {}
  ) {
    return callApi<T>({ ...opts, method: 'PUT', url, body });
  },
  patch<T>(
    url: string,
    body?: unknown,
    opts: Omit<CallApiOptions, 'url' | 'method' | 'body'> = {}
  ) {
    return callApi<T>({ ...opts, method: 'PATCH', url, body });
  },
  delete<T>(url: string, opts: Omit<CallApiOptions, 'url' | 'method'> = {}) {
    return callApi<T>({ ...opts, method: 'DELETE', url });
  },
};

/**
 * Generic fallback for displaying an error when the feature has no
 * domain-specific copy map yet. Prefer building a feature-local
 * resolver (see docs) that consults the feature's own `ERROR_COPY`
 * before falling back here.
 */
export function resolveErrorMessage(err: unknown, fallback?: string): string {
  if (err instanceof ApiError) {
    if (err.errors.length > 0) return err.errors[0];
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback ?? 'Something went wrong. Please try again.';
}
